/**
 * VerticalRecorder — 9:16 portrait variant of the replay video, sized for
 * TikTok / Instagram Reels / WhatsApp Status / YouTube Shorts.
 *
 * Output: 720×1280 webm (or mp4 when supported).
 *
 * Layout (top → bottom):
 *
 *     ┌───────────────────────┐  0
 *     │   TOP BRANDING BAND   │  280px:
 *     │   "CURVLE" wordmark   │   - amber wordmark
 *     │   tagline             │   - "DAILY · WORLD CUP 2026"
 *     ├───────────────────────┤  280
 *     │                       │
 *     │   GAME REPLAY 720×720 │  720px:
 *     │   (composited from    │   - PitchRenderer drawn to a 720×720
 *     │    sub-canvas every   │     sub-canvas, drawImage'd into the
 *     │    frame)             │     middle band each tick.
 *     │                       │
 *     ├───────────────────────┤  1000
 *     │   BOTTOM BAND         │  280px:
 *     │   flag + name | score │   - country flag PNG (flagcdn.com) +
 *     │   curvle.world/c/<id> │     player display name + score badge
 *     │                       │   - share URL strip
 *     └───────────────────────┘  1280
 *
 * After the replay ends we hold the final frame for 0.4s then run a
 * 1.6s end card: emerald wash fades up, big score reveals, "Beat me —
 * open the link" CTA, and the challenge URL rendered large enough to be
 * readable in any platform's compressed feed (so screen-recording
 * reshares still preserve a path back to play). The clickable URL
 * itself rides along in the share's navigator.share `url` field —
 * WhatsApp / iMessage / Telegram / X turn it into a clickable link
 * automatically; viewers who only have the pixels can type it in.
 *
 * Why a separate file (not extending recorder.ts):
 *   recorder.ts is the canonical 1:1 path. This file is the 9:16 path.
 *   Keeping them separate avoids a giant parameterised function and lets
 *   each layout evolve independently. Shared helpers (mimeType pick,
 *   captureStream support detection, withInertKeeper) re-imported from
 *   recorder.ts and ../game/types.
 */
import { PitchRenderer, type SceneInputs } from "./render";
import { simulate } from "./engine";
import { canRecordVideo } from "./recorder";
import type {
  AimInput,
  KeeperReveal,
  Outcome,
  PublicPuzzleParams,
  PuzzleParams,
  Vec3,
} from "./types";
import { flagUrl } from "@/lib/countries";

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------
const W = 720;
const H = 1280;
const TOP_H = 280;
const GAME_H = 720;
// const BOTTOM_H = H - TOP_H - GAME_H; // 280 — implicit from layout
const FPS = 30;
const MAX_REPLAY_SEC = 2.6;
const POST_OUTCOME_HOLD_SEC = 0.4;
const END_CARD_SEC = 1.6;

// Curvle palette
const C_BG_DARK = "#022c22";       // emerald-950
const C_BG_MID = "#064e3b";        // emerald-900
const C_AMBER = "#fcd34d";         // amber-300
const C_WHITE = "#ffffff";
const C_DIM = "rgba(255,255,255,0.65)";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export interface VerticalRecorderOptions {
  params: PublicPuzzleParams;
  aim: AimInput;
  keeperReveal: KeeperReveal;
  /** Player display name for the bottom band ("Balarama", anonymous fallback). */
  playerName: string;
  /** ISO 3166-1 alpha-2 for the bottom-band flag. "XX" → no flag, just name. */
  countryCode: string;
  /** Outcome from the server (drives end-card copy). */
  outcome: Outcome;
  /** Final score 0–1000. */
  score: number;
  /** Challenge URL: https://api.curvle.world/c/<attemptId>. The QR code +
   *  bottom-band URL strip + share text all derive from this. */
  challengeUrl: string;
}

export interface VerticalRecorderResult {
  blob: Blob;
  mimeType: string;
  durationSec: number;
}

/**
 * Render a 9:16 portrait MP4/webm of an attempt. Falls back to throwing
 * if MediaRecorder unavailable — caller should branch to the 1:1 path or
 * the PNG still in that case.
 */
export async function recordAttemptVertical(
  opts: VerticalRecorderOptions,
): Promise<VerticalRecorderResult> {
  if (!canRecordVideo()) {
    throw new Error("VerticalRecorder: MediaRecorder unavailable in this browser");
  }

  // ---- 1. Resources we may need across the run -----------------------------
  // Pre-load the country flag PNG BEFORE we start recording, so the
  // recorder doesn't capture loading-flicker frames. (We dropped the QR
  // in favour of a big URL text — clickable when the share is delivered
  // via navigator.share's url field, readable when burned into pixels.)
  const flagBitmap = await loadFlagBitmap(opts.countryCode);

  // ---- 2. Off-DOM 9:16 main canvas (this is what MediaRecorder captures) ---
  const host = document.createElement("div");
  host.style.cssText =
    "position:fixed;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden;pointer-events:none;";
  const mainCanvas = document.createElement("canvas");
  mainCanvas.width = W;
  mainCanvas.height = H;
  host.appendChild(mainCanvas);
  document.body.appendChild(host);

  const mainCtx = mainCanvas.getContext("2d");
  if (!mainCtx) {
    document.body.removeChild(host);
    throw new Error("VerticalRecorder: 2d context unavailable");
  }

  // ---- 3. Sub-canvas for the game frames ----------------------------------
  const gameCanvas = document.createElement("canvas");
  gameCanvas.width = GAME_H;
  gameCanvas.height = GAME_H;
  const gameCtx = gameCanvas.getContext("2d");
  if (!gameCtx) {
    document.body.removeChild(host);
    throw new Error("VerticalRecorder: 2d context unavailable on sub-canvas");
  }
  gameCtx.setTransform(1, 0, 0, 1, 0, 0);

  // PitchRenderer expects a CanvasView shape — give it one bound to the
  // sub-canvas at 1 DPR.
  const view = {
    ctx: gameCtx,
    cssWidth: GAME_H,
    cssHeight: GAME_H,
    dpr: 1,
    dispose: () => undefined,
  };
  const renderer = new PitchRenderer(view, opts.params);

  // ---- 4. MediaRecorder on the MAIN canvas --------------------------------
  let recorder: MediaRecorder | null = null;
  try {
    const stream = (mainCanvas as unknown as {
      captureStream: (frameRate?: number) => MediaStream;
    }).captureStream(FPS);
    const mimeType = pickMimeType();
    recorder = new MediaRecorder(stream, {
      mimeType,
      // Higher bitrate than the 1:1 recorder — 9:16 has ~3× the pixel
      // count and we want clean text on the branding bands.
      videoBitsPerSecond: 1_400_000,
    });
    const chunks: Blob[] = [];
    const recordingDone = new Promise<Blob>((resolve, reject) => {
      recorder!.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      recorder!.onstop = () => resolve(new Blob(chunks, { type: mimeType }));
      recorder!.onerror = (e) => reject((e as ErrorEvent).error ?? e);
    });

    // ---- 5. Pre-paint static branding (overwritten every frame for safety) -
    paintTopBand(mainCtx);
    paintBottomBand(mainCtx, opts, flagBitmap);

    recorder.start(/* timeslice */ 100);

    // ---- 6. Run the replay loop -----------------------------------------
    const sim = simulate(opts.aim, withInertKeeper(opts.params));
    const trajectory: Vec3[] = sim.trajectory;
    const trajectoryDuration = Math.min(
      MAX_REPLAY_SEC,
      Math.max(0.4, sim.hitTime || trajectory.length / 60),
    );
    const replayDuration = Math.min(
      MAX_REPLAY_SEC + POST_OUTCOME_HOLD_SEC,
      trajectoryDuration + POST_OUTCOME_HOLD_SEC,
    );

    const startWall = performance.now();
    await new Promise<void>((resolve) => {
      const tick = (now: number): void => {
        const elapsed = (now - startWall) / 1000;

        // Sample ball position from trajectory at current time.
        const idx = Math.min(
          trajectory.length - 1,
          Math.floor(Math.min(elapsed, trajectoryDuration) * 60),
        );
        const ball: Vec3 = trajectory[idx] ?? { x: 0, y: 0.11, z: 0 };
        const scene: SceneInputs = {
          ball,
          replayTime: elapsed,
          phase: "REPLAYING",
          aim: null,
          preview: null,
          keeperReveal: opts.keeperReveal,
        };

        // Render the game frame onto the sub-canvas.
        renderer.draw(scene);

        // Composite onto main canvas. Repaint bands too — the game canvas
        // has alpha pixels at edges and we want clean band borders.
        paintTopBand(mainCtx);
        mainCtx.drawImage(gameCanvas, 0, TOP_H, W, GAME_H);
        paintBottomBand(mainCtx, opts, flagBitmap);

        if (elapsed >= replayDuration) {
          resolve();
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });

    // ---- 7. End card (1.6s) ---------------------------------------------
    await runEndCard(mainCtx, opts);

    // ---- 8. Stop and resolve --------------------------------------------
    recorder.stop();
    const blob = await recordingDone;
    return {
      blob,
      mimeType,
      durationSec: replayDuration + END_CARD_SEC,
    };
  } finally {
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch { /* noop */ }
    }
    document.body.removeChild(host);
  }
}

// ---------------------------------------------------------------------------
// Paint helpers — top band, bottom band, end card
// ---------------------------------------------------------------------------

function paintTopBand(ctx: CanvasRenderingContext2D): void {
  // Background: dark emerald gradient
  const grad = ctx.createLinearGradient(0, 0, 0, TOP_H);
  grad.addColorStop(0, C_BG_DARK);
  grad.addColorStop(1, C_BG_MID);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, TOP_H);

  // Curvle wordmark — large amber, centred
  ctx.font = "900 92px system-ui, -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = C_AMBER;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("CURVLE", W / 2, 130);

  // Tagline
  ctx.font = "700 24px system-ui, -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = C_DIM;
  ctx.letterSpacing = "4px"; // not all browsers honor this on canvas; harmless
  ctx.fillText("DAILY · WORLD CUP 2026", W / 2, 175);

  // Subtle separator at bottom
  ctx.fillStyle = "rgba(252,211,77,0.5)"; // amber/50
  ctx.fillRect(0, TOP_H - 2, W, 2);

  // Decorative ball icon at top-left
  drawBallIcon(ctx, 60, 70, 22);
}

function paintBottomBand(
  ctx: CanvasRenderingContext2D,
  opts: VerticalRecorderOptions,
  flagBitmap: ImageBitmap | HTMLImageElement | null,
): void {
  const y0 = TOP_H + GAME_H; // 1000
  const grad = ctx.createLinearGradient(0, y0, 0, H);
  grad.addColorStop(0, C_BG_MID);
  grad.addColorStop(1, C_BG_DARK);
  ctx.fillStyle = grad;
  ctx.fillRect(0, y0, W, H - y0);

  // Top edge highlight
  ctx.fillStyle = "rgba(252,211,77,0.5)";
  ctx.fillRect(0, y0, W, 2);

  // ----- Top half of bottom band: flag + name | score badge -----
  const rowMidY = y0 + 70; // 1070

  // Flag (left side)
  let textX = 40;
  if (flagBitmap) {
    const fW = 60;
    const fH = 45;
    drawRoundedImage(ctx, flagBitmap, 40, rowMidY - fH / 2, fW, fH, 4);
    textX = 40 + fW + 18;
  }

  // Player name (right of flag)
  ctx.font = "700 32px system-ui, -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = C_WHITE;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(truncate(opts.playerName, 18), textX, rowMidY);

  // Score chip (right side)
  const chipW = 180;
  const chipH = 80;
  const chipX = W - chipW - 40;
  const chipY = rowMidY - chipH / 2;
  drawRoundedRect(ctx, chipX, chipY, chipW, chipH, 16, "rgba(252,211,77,0.16)");
  ctx.strokeStyle = "rgba(252,211,77,0.5)";
  ctx.lineWidth = 2;
  drawRoundedRect(ctx, chipX, chipY, chipW, chipH, 16, undefined, true);

  ctx.font = "900 56px system-ui, -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = C_AMBER;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(opts.score), chipX + chipW / 2, rowMidY + 4);

  // ----- Bottom half: URL strip -----
  const urlBarY = y0 + 160;
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.fillRect(0, urlBarY, W, 80);

  ctx.font = "700 26px system-ui, -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = C_AMBER;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // We don't show the full URL — recipients will scan the QR or
  // remember the home page. "Beat me on curvle.world" is the CTA.
  ctx.fillText("▶  Beat me on curvle.world", W / 2, urlBarY + 40);
}

async function runEndCard(
  ctx: CanvasRenderingContext2D,
  opts: VerticalRecorderOptions,
): Promise<void> {
  const startWall = performance.now();
  const total = END_CARD_SEC;

  // Display URL: human-readable form, www host, short attempt-id slug.
  // Comes from the challengeUrl passed in (which may be api.curvle.world
  // for the share-link itself); we extract the path and render the human
  // version. If the slug is missing (anonymous attempt), just show the
  // root domain so screen-recording reshares still land somewhere.
  const displayUrl = humanizeChallengeUrl(opts.challengeUrl);

  return new Promise<void>((resolve) => {
    const tick = (now: number): void => {
      const t = (now - startWall) / 1000;
      const progress = Math.min(1, t / total);
      // Ease-out for fade-in
      const fade = Math.min(1, progress * 1.4);

      // Snapshot the previous frame underneath, then overlay an emerald
      // wash that ramps from 0 to ~0.95 alpha. Cheap scene transition.
      ctx.fillStyle = `rgba(2,44,34,${0.55 + 0.40 * fade})`;
      ctx.fillRect(0, 0, W, H);

      const cx = W / 2;

      // Wordmark
      ctx.font = "900 80px system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.fillStyle = `rgba(252,211,77,${fade})`;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText("CURVLE", cx, 220);

      // Outcome label
      ctx.font = "700 28px system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.fillStyle = `rgba(255,255,255,${0.85 * fade})`;
      ctx.fillText(outcomeLabel(opts.outcome), cx, 270);

      // Score — count up over the first ~0.4s of the card, then steady
      const scoreProgress = Math.min(1, progress / 0.4);
      const scoreShown = Math.round(opts.score * scoreProgress);
      ctx.font = "900 200px system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.fillStyle = `rgba(252,211,77,${fade})`;
      ctx.fillText(String(scoreShown), cx, 510);
      ctx.font = "700 26px system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.fillStyle = `rgba(255,255,255,${0.7 * fade})`;
      ctx.fillText("/ 1000", cx, 555);

      // CTA strip
      ctx.font = "800 38px system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.fillStyle = `rgba(255,255,255,${fade})`;
      ctx.fillText("Beat me — open the link", cx, 760);

      // URL block — fades in slightly later so it lands as the eye reaches
      // the bottom half of the card. This is the take-away viewers
      // memorise and what screen-recording reshares preserve in pixels.
      const urlFade = Math.max(0, Math.min(1, (progress - 0.15) / 0.45));

      // Rounded amber-tinted backing
      const urlW = 580;
      const urlH = 110;
      const urlX = cx - urlW / 2;
      const urlY = 830;
      ctx.fillStyle = `rgba(252,211,77,${0.18 * urlFade})`;
      drawRoundedRect(ctx, urlX, urlY, urlW, urlH, 22);
      ctx.strokeStyle = `rgba(252,211,77,${0.55 * urlFade})`;
      ctx.lineWidth = 3;
      drawRoundedRect(ctx, urlX, urlY, urlW, urlH, 22, undefined, true);

      // The URL itself — large, monospace-ish, amber. Auto-shrink if the
      // attempt-id slug is unusually long.
      const url = displayUrl;
      let urlFontSize = 44;
      ctx.font = `900 ${urlFontSize}px system-ui, -apple-system, 'Segoe UI', sans-serif`;
      while (ctx.measureText(url).width > urlW - 40 && urlFontSize > 24) {
        urlFontSize -= 2;
        ctx.font = `900 ${urlFontSize}px system-ui, -apple-system, 'Segoe UI', sans-serif`;
      }
      ctx.fillStyle = `rgba(252,211,77,${urlFade})`;
      ctx.textBaseline = "middle";
      ctx.fillText(url, cx, urlY + urlH / 2);
      ctx.textBaseline = "alphabetic";

      // Tag line under URL
      ctx.font = "700 22px system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.fillStyle = `rgba(255,255,255,${0.7 * urlFade})`;
      ctx.fillText("Type it in or tap the link below ↓", cx, urlY + urlH + 40);

      if (t >= total) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/**
 * Convert the share-link form (e.g. `https://api.curvle.world/c/abc123`)
 * to the human-typeable form rendered into video pixels (`curvle.world/c/abc123`).
 * If parsing fails, fall back to bare `curvle.world`.
 */
function humanizeChallengeUrl(input: string): string {
  try {
    const u = new URL(input);
    const path = u.pathname.replace(/^\/+/, "");
    return path ? `curvle.world/${path}` : "curvle.world";
  } catch {
    return "curvle.world";
  }
}

// ---------------------------------------------------------------------------
// Drawing primitives
// ---------------------------------------------------------------------------

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill?: string,
  strokeOnly?: boolean,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  if (strokeOnly) {
    ctx.stroke();
  } else if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  } else {
    ctx.fill();
  }
}

function drawRoundedImage(
  ctx: CanvasRenderingContext2D,
  img: ImageBitmap | HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(img, x, y, w, h);
  // Subtle stroke for legibility on light flag colours
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

function drawBallIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
): void {
  ctx.save();
  ctx.fillStyle = C_WHITE;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = C_BG_DARK;
  // simple panel hint
  ctx.beginPath();
  ctx.moveTo(cx, cy - r * 0.5);
  ctx.lineTo(cx + r * 0.42, cy - r * 0.05);
  ctx.lineTo(cx + r * 0.25, cy + r * 0.45);
  ctx.lineTo(cx - r * 0.25, cy + r * 0.45);
  ctx.lineTo(cx - r * 0.42, cy - r * 0.05);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Async resource loaders
// ---------------------------------------------------------------------------

async function loadFlagBitmap(
  cc: string,
): Promise<ImageBitmap | HTMLImageElement | null> {
  const url = flagUrl(cc, 160); // 160px wide enough for 60×45 px display
  if (!url) return null;
  try {
    // Cross-origin image with anonymous CORS so canvas isn't tainted.
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("flag load failed"));
    });
    return img;
  } catch {
    // eslint-disable-next-line no-console
    console.warn("[curvle.vertical] flag load failed for", cc, "— omitting");
    return null;
  }
}

// ---------------------------------------------------------------------------
// Misc helpers
// ---------------------------------------------------------------------------

function pickMimeType(): string {
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4",
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "video/webm";
}

function withInertKeeper(p: PublicPuzzleParams): PuzzleParams {
  return {
    ...p,
    keeper: {
      restPos: p.keeper.restPos,
      diveTo: p.keeper.restPos,
      reactionTime: 999,
      diveDuration: 1,
      reach: 0,
    },
  };
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + "…";
}

function outcomeLabel(o: Outcome): string {
  switch (o) {
    case "GOAL":
      return "GOAL";
    case "SAVED":
      return "SAVED";
    case "POST":
      return "OFF THE POST";
    case "WALL":
      return "BLOCKED BY WALL";
    case "OVER":
      return "OVER THE BAR";
    case "WIDE":
      return "WIDE";
    default:
      return String(o).toUpperCase();
  }
}
